import React, { useEffect, useRef, useState } from 'react';

import { CloudDownloadOutlined, FileExcelOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Row,
  Select,
  Spin,
  Table,
  message,
} from 'antd';
import type { TableProps } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';

import { ReportSummaryRow } from 'models/ReportSummaryRow';

import {
  DiReportService,
  GetSummaryReportRequestDto,
} from 'services/DiReportService';

import Layout from '../../../components/Layout/Layout';
import appI18n from '../../../config/i18n';
import DownloadCsvModal from './Components/DownloadCsvModal';

import './DataQuery.scss';

const { Option } = Select;
const { RangePicker } = DatePicker;

interface DataItem {
  groupedby?: string;
  isTotal?: boolean;
  total?: string | number;
  [key: string]: string | number | boolean | undefined;
}

function DataQuery() {
  // Referencia al contenedor de filtros para calcular su altura
  const filtersContainerRef = useRef<HTMLDivElement>(null);

  // Hook para mensajes
  const [messageApi, contextHolder] = message.useMessage();
  const { t } = useTranslation(undefined, { i18n: appI18n });
  const dateFormat: string = useSelector(
    (state: any) => state.intl?.dateFormat
  );

  // Detectar si es pantalla grande
  const isLargeScreen = useMediaQuery({ query: '(min-width: 1024px)' });

  // Estado para altura dinámica de la tabla
  const [tableHeight, setTableHeight] = useState<number | string>(450);

  // Calcular el rango de la semana actual
  const today = dayjs();
  const startOfWeek = today.startOf('week');
  const endOfWeek = today.endOf('week');

  // Estado para los filtros
  const [metric, setMetric] = useState<string>('SELLOUT_QTY');
  const [category, setCategory] = useState<string>('CARRIER');
  const [appliedCategory, setAppliedCategory] = useState<string>('CARRIER'); // Categoría aplicada actualmente
  const [period, setPeriod] = useState<string>('DAILY');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(
    [startOfWeek, endOfWeek]
  );
  const [selectedYear, setSelectedYear] = useState<number | null>(
    dayjs().year()
  );
  const [isApplyDisabled, setIsApplyDisabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [reportData, setReportData] = useState<ReportSummaryRow[]>([]);
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);

  // Estado para guardar los filtros aplicados
  const [appliedFilters, setAppliedFilters] = useState<{
    metric: string;
    category: string;
    period: string;
    dateRange: [dayjs.Dayjs, dayjs.Dayjs] | null;
    selectedYear: number | null;
  }>({
    metric: 'SELLOUT_QTY',
    category: 'CARRIER',
    period: 'DAILY',
    dateRange: [startOfWeek, endOfWeek],
    selectedYear: dayjs().year(),
  });

  // Estado para controlar la visibilidad del modal de descarga CSV
  const [isDownloadCsvModalVisible, setIsDownloadCsvModalVisible] =
    useState(false);

  // Generar opciones de año (año actual - 5 años)
  const currentYear = dayjs().year();
  const yearOptions = [];
  for (let i = 0; i <= 5; i++) {
    const year = currentYear - i;
    yearOptions.push({
      value: year.toString(),
      label: year.toString(),
    });
  }

  // Verificar si el botón aplicar debe estar habilitado
  useEffect(() => {
    if (period === 'DAILY' && dateRange) {
      setIsApplyDisabled(false);
    } else if (period === 'MONTHLY' && selectedYear) {
      setIsApplyDisabled(false);
    } else {
      setIsApplyDisabled(true);
    }
  }, [period, dateRange, selectedYear]);

  // Inicializar valores al montar el componente
  useEffect(() => {
    // Inicializar con la semana actual para DAILY (por defecto)
    if (period === 'DAILY') {
      setDateRange([startOfWeek, endOfWeek]);
    }
  }, []);

  // Efecto para calcular la altura de la tabla en pantallas grandes
  useEffect(() => {
    const calculateTableHeight = () => {
      if (isLargeScreen && filtersContainerRef.current) {
        // Obtener altura de la ventana
        const windowHeight = window.innerHeight;

        // Obtener altura del contenedor de filtros
        const filtersHeight = filtersContainerRef.current.clientHeight;

        // Calcular altura disponible (ajuste más preciso)
        // MENU_HEIGHT (64) + altura de cards y márgenes (90)
        const availableHeight = windowHeight - 64 - filtersHeight - 90;

        // Establecer altura ajustada para evitar scroll del contenedor padre
        setTableHeight(Math.min(availableHeight, 450));
      } else {
        // En pantallas pequeñas, usar un valor fijo
        setTableHeight(450);
      }
    };

    // Calcular al montar y cuando cambie el tamaño de la ventana
    calculateTableHeight();
    window.addEventListener('resize', calculateTableHeight);

    return () => {
      window.removeEventListener('resize', calculateTableHeight);
    };
  }, [isLargeScreen]);

  // Función para manejar la llamada a la API
  const fetchReportData = async () => {
    setLoading(true);
    try {
      let requestBody: GetSummaryReportRequestDto;

      if (period === 'DAILY' && dateRange) {
        requestBody = {
          type: 'DAILY',
          category: category as 'CARRIER' | 'SKU' | 'STORE',
          metric: metric as 'SELLOUT_QTY' | 'SELLOUT_AMOUNT' | 'STOCK_QTY',
          startDate: dateRange[0].format('YYYY-MM-DD'),
          endDate: dateRange[1].format('YYYY-MM-DD'),
        };
      } else if (period === 'MONTHLY' && selectedYear) {
        // Para MONTHLY, enviamos el primer y último día del año seleccionado
        const startDate = `${selectedYear}-01-01`;
        const endDate = `${selectedYear}-12-31`;

        requestBody = {
          type: 'MONTHLY',
          category: category as 'CARRIER' | 'SKU' | 'STORE',
          metric: metric as 'SELLOUT_QTY' | 'SELLOUT_AMOUNT' | 'STOCK_QTY',
          startDate,
          endDate,
        };
      } else {
        messageApi.error(
          t('scenes.data_ingester.data_query.scene.errors.incomplete_filters')
        );
        setLoading(false);
        return;
      }

      const response = await DiReportService.getSummary(requestBody);

      if (response.success && response.data) {
        setReportData(response.data as ReportSummaryRow[]);
      } else {
        messageApi.error(
          t('scenes.data_ingester.data_query.scene.errors.fetch_report')
        );
      }
    } catch (error) {
      console.error('Error al obtener los datos del reporte:', error);
      messageApi.error(
        t('scenes.data_ingester.data_query.scene.errors.fetch_report')
      );
    } finally {
      setLoading(false);
    }
  };

  // Manejar clic en el botón Aplicar
  const handleApplyFilter = () => {
    setAppliedCategory(category); // Actualizar la categoría aplicada

    // Guardar todos los filtros aplicados
    setAppliedFilters({
      metric,
      category,
      period,
      dateRange,
      selectedYear,
    });

    setHasAppliedFilter(true);
    fetchReportData();
  };

  // Manejar cambio de periodo
  const handlePeriodChange = (value: string) => {
    setPeriod(value);
    // Restablecer valores al cambiar el periodo
    setDateRange(null);
    setSelectedYear(null);
  };

  // Efecto para cargar datos iniciales al montar el componente
  useEffect(() => {
    // Aplicar filtro inicial automáticamente
    fetchReportData();

    // Inicializar los filtros aplicados con los valores iniciales
    setAppliedFilters({
      metric,
      category,
      period,
      dateRange,
      selectedYear,
    });

    setHasAppliedFilter(true);
  }, []);

  // Función para generar columnas de tabla dinámicas basadas en los datos del informe
  const generateColumns = (): TableProps<DataItem>['columns'] => {
    if (!reportData || reportData.length === 0) {
      return [];
    }

    // Definir anchos estándar para columnas
    const firstColumnWidth = 200;
    const dateColumnWidth = 120;
    const totalColumnWidth = 140;

    const columns: TableProps<DataItem>['columns'] = [
      {
        title:
          appliedCategory === 'CARRIER'
            ? t(
                'scenes.data_ingester.data_query.scene.dropdown.category.options.carrier'
              )
            : appliedCategory === 'SKU'
            ? t(
                'scenes.data_ingester.data_query.scene.dropdown.category.options.sku'
              )
            : t(
                'scenes.data_ingester.data_query.scene.dropdown.category.options.store'
              ),
        dataIndex: 'groupedby',
        key: 'groupedby',
        fixed: 'left',
        width: firstColumnWidth,
        render: (text, record) => {
          return {
            props: {
              className: record.isTotal ? 'summary-row' : '',
            },
            children: <span>{text}</span>,
          };
        },
      },
    ];

    // Extraer todas las posibles claves de fecha/mes excluyendo 'groupedby' y 'total'
    const dateKeys = Object.keys(reportData[0])
      .filter((key) => key !== 'groupedby' && key !== 'total')
      .sort((a, b) => {
        // Para DAILY, ordenar por fecha
        if (period === 'DAILY') {
          return new Date(a).getTime() - new Date(b).getTime();
        }
        // Para MONTHLY, ordenar por número de mes
        return parseInt(a) - parseInt(b);
      });

    // Añadir columnas para cada fecha/mes
    dateKeys.forEach((key) => {
      let title = key;

      // Formatear encabezados de columna
      if (period === 'DAILY') {
        title = dayjs(key).format(dateFormat);
      } else if (period === 'MONTHLY') {
        const monthIndex = parseInt(key) - 1;
        if (monthIndex === 0)
          title = t('scenes.data_ingester.data_query.scene.months.january');
        else if (monthIndex === 1)
          title = t('scenes.data_ingester.data_query.scene.months.february');
        else if (monthIndex === 2)
          title = t('scenes.data_ingester.data_query.scene.months.march');
        else if (monthIndex === 3)
          title = t('scenes.data_ingester.data_query.scene.months.april');
        else if (monthIndex === 4)
          title = t('scenes.data_ingester.data_query.scene.months.may');
        else if (monthIndex === 5)
          title = t('scenes.data_ingester.data_query.scene.months.june');
        else if (monthIndex === 6)
          title = t('scenes.data_ingester.data_query.scene.months.july');
        else if (monthIndex === 7)
          title = t('scenes.data_ingester.data_query.scene.months.august');
        else if (monthIndex === 8)
          title = t('scenes.data_ingester.data_query.scene.months.september');
        else if (monthIndex === 9)
          title = t('scenes.data_ingester.data_query.scene.months.october');
        else if (monthIndex === 10)
          title = t('scenes.data_ingester.data_query.scene.months.november');
        else if (monthIndex === 11)
          title = t('scenes.data_ingester.data_query.scene.months.december');
      }

      columns.push({
        title,
        dataIndex: key,
        key,
        align: 'right',
        width: dateColumnWidth,
        render: (value, record) => {
          // Asegurar que los valores numéricos se muestren con el formato adecuado
          const numValue = parseFloat(value as string);
          return {
            props: {
              className: record.isTotal ? 'summary-row' : '',
            },
            children: (
              <span>{isNaN(numValue) ? value : formatNumber(numValue)}</span>
            ),
          };
        },
      });
    });

    // Añadir columna de total al final
    columns.push({
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      fixed: 'right',
      width: totalColumnWidth,
      render: (value, record) => {
        return {
          props: {
            className: record.isTotal ? 'summary-row' : '',
          },
          children: (
            <span>
              {typeof value === 'number' || !isNaN(parseFloat(value as string))
                ? formatNumber(value)
                : value}
            </span>
          ),
        };
      },
    });

    return columns;
  };

  // Función para formatear números con separador de miles y 2 decimales
  const formatNumber = (value: number | string): string => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '';

    // Formatear con separador de miles (,) y punto decimal (.) con 2 decimales fijos
    return numValue.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Preparar datos para la tabla incluyendo totales
  const prepareTableData = (): DataItem[] => {
    if (!reportData || reportData.length === 0) {
      return [];
    }

    // Extraer todas las columnas de fecha/periodo excluyendo 'groupedby'
    const dateKeys = Object.keys(reportData[0])
      .filter((key) => key !== 'groupedby')
      .sort((a, b) => {
        if (period === 'DAILY') {
          return new Date(a).getTime() - new Date(b).getTime();
        }
        return parseInt(a) - parseInt(b);
      });

    // Transformar la respuesta de la API para que coincida con la estructura de la tabla y añadir totales de fila
    const transformedData = reportData.map((row) => {
      const transformedRow: DataItem = {};

      // Copiar campo groupedby
      if (row.groupedBy) {
        transformedRow['groupedby'] = row.groupedBy;
      } else {
        transformedRow['groupedby'] = (row['groupedby'] || '') as any;
      }

      // Copiar todos los valores de fecha/periodo y calcular el total de la fila
      let rowTotal = 0;
      dateKeys.forEach((key) => {
        const value = row[key];
        // Asegurar que sea un número para el cálculo
        const numValue = parseFloat(value as string);
        if (isNaN(numValue)) {
          transformedRow[key] = value;
        } else {
          transformedRow[key] = numValue;
        }
        if (!isNaN(numValue)) {
          rowTotal += numValue;
        }
      });

      // Añadir total de fila
      transformedRow['total'] = rowTotal;

      return transformedRow;
    });

    // Calcular totales de columna
    const totalsRow: DataItem = {
      groupedby: t('scenes.data_ingester.data_query.scene.table.columns.total'),
      isTotal: true,
    };

    // Sumar cada columna incluyendo los totales de fila
    dateKeys.forEach((key) => {
      let columnTotal = 0;
      transformedData.forEach((row) => {
        const valueAsString = row[key]?.toString() || '0';
        const value = parseFloat(valueAsString);
        if (!isNaN(value)) {
          columnTotal += value;
        }
      });
      totalsRow[key] = columnTotal;
    });

    // Calcular gran total (suma de todos los totales de fila)
    let grandTotal = 0;
    transformedData.forEach((row) => {
      const totalAsString = row.total?.toString() || '0';
      const total = parseFloat(totalAsString);
      if (!isNaN(total)) {
        grandTotal += total;
      }
    });
    totalsRow['total'] = grandTotal;

    // Añadir fila de totales al final
    return [...transformedData, totalsRow];
  };

  // Función para manejar la descarga de archivo Excel
  const handleDownloadExcel = async () => {
    try {
      const messageKey = 'downloading';
      messageApi.loading({
        content: t(
          'scenes.data_ingester.data_query.scene.messages.downloading_file'
        ),
        key: messageKey,
        duration: 0,
      });

      let requestBody: GetSummaryReportRequestDto;

      if (appliedFilters.period === 'DAILY' && appliedFilters.dateRange) {
        requestBody = {
          type: 'DAILY',
          category: appliedFilters.category as 'CARRIER' | 'SKU' | 'STORE',
          metric: appliedFilters.metric as
            | 'SELLOUT_QTY'
            | 'SELLOUT_AMOUNT'
            | 'STOCK_QTY',
          startDate: appliedFilters.dateRange[0].format('YYYY-MM-DD'),
          endDate: appliedFilters.dateRange[1].format('YYYY-MM-DD'),
        };
      } else if (
        appliedFilters.period === 'MONTHLY' &&
        appliedFilters.selectedYear
      ) {
        // Para MONTHLY, enviamos el primer y último día del año seleccionado
        const startDate = `${appliedFilters.selectedYear}-01-01`;
        const endDate = `${appliedFilters.selectedYear}-12-31`;

        requestBody = {
          type: 'MONTHLY',
          category: appliedFilters.category as 'CARRIER' | 'SKU' | 'STORE',
          metric: appliedFilters.metric as
            | 'SELLOUT_QTY'
            | 'SELLOUT_AMOUNT'
            | 'STOCK_QTY',
          startDate,
          endDate,
        };
      } else {
        messageApi.error(
          t('scenes.data_ingester.data_query.scene.errors.incomplete_filters')
        );
        return;
      }

      const response = await DiReportService.getXlsxSummary(requestBody);

      if (response.success && response.data) {
        const data = response.data as { blob: Blob; filename: string };
        // Crear un enlace temporal para la descarga
        const url = window.URL.createObjectURL(data.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        // Destruir mensaje y mostrar éxito
        messageApi.destroy(messageKey);
        messageApi.success(
          t('scenes.data_ingester.data_query.scene.messages.download_success')
        );
      } else {
        messageApi.error({
          content: t(
            'scenes.data_ingester.data_query.scene.errors.download_file'
          ),
          key: messageKey,
        });
      }
    } catch (error) {
      console.error('Error al descargar el archivo:', error);
      messageApi.error(
        t('scenes.data_ingester.data_query.scene.errors.download_file')
      );
    }
  };

  // Obtener columnas dinámicas
  const tableColumns = generateColumns();

  // Calcular ancho de desplazamiento horizontal en base al número de columnas
  const scrollXWidth =
    (tableColumns?.reduce((acc, col) => acc + ((col as any).width || 120), 0) ||
      0) + 200; // margen extra

  // Obtener datos dinámicos
  const tableData = prepareTableData();

  // Constante para la altura del menú fijo (ajustar según sea necesario)
  const MENU_HEIGHT = 64;

  // Configuración para sticky header y columnas fijas
  const stickyConfig = {
    offsetHeader: MENU_HEIGHT,
    offsetScroll: 0,
    getContainer: () => document.querySelector('.ant-layout-content'),
  } as any;

  return (
    <Layout loading={loading && !hasAppliedFilter} title={''}>
      {contextHolder}
      <div className="data-query-container">
        <div ref={filtersContainerRef}>
          <Card>
            <Row gutter={[16, 16]} className="filter-row">
              {/* Métrica */}
              <Col>
                <span className="filter-label">
                  {t('scenes.data_ingester.data_query.scene.labels.metric')}
                </span>
                <Select
                  value={metric}
                  className="filter-select"
                  onChange={(value) => setMetric(value)}
                >
                  <Option value="SELLOUT_QTY">
                    {t(
                      'scenes.data_ingester.data_query.scene.dropdown.metric.options.sellout_qty'
                    )}
                  </Option>
                  <Option value="SELLOUT_AMOUNT">
                    {t(
                      'scenes.data_ingester.data_query.scene.dropdown.metric.options.sellout_amount'
                    )}
                  </Option>
                  <Option value="STOCK_QTY">
                    {t(
                      'scenes.data_ingester.data_query.scene.dropdown.metric.options.stock_qty'
                    )}
                  </Option>
                </Select>
              </Col>

              {/* Categoría */}
              <Col>
                <span className="filter-label">
                  {t('scenes.data_ingester.data_query.scene.labels.category')}
                </span>
                <Select
                  value={category}
                  className="filter-select"
                  onChange={(value) => setCategory(value)}
                >
                  <Option value="CARRIER">
                    {t(
                      'scenes.data_ingester.data_query.scene.dropdown.category.options.carrier'
                    )}
                  </Option>
                  <Option value="SKU">
                    {t(
                      'scenes.data_ingester.data_query.scene.dropdown.category.options.sku'
                    )}
                  </Option>
                  <Option value="STORE">
                    {t(
                      'scenes.data_ingester.data_query.scene.dropdown.category.options.store'
                    )}
                  </Option>
                </Select>
              </Col>

              {/* Periodo */}
              <Col>
                <span className="filter-label">
                  {t('scenes.data_ingester.data_query.scene.labels.period')}
                </span>
                <Select
                  value={period}
                  className="filter-select"
                  onChange={handlePeriodChange}
                >
                  <Option value="MONTHLY">
                    {t(
                      'scenes.data_ingester.data_query.scene.dropdown.period.options.monthly'
                    )}
                  </Option>
                  <Option value="DAILY">
                    {t(
                      'scenes.data_ingester.data_query.scene.dropdown.period.options.daily'
                    )}
                  </Option>
                </Select>
              </Col>

              {/* Campos condicionales basados en la selección de periodo */}
              {period === 'DAILY' && (
                <Col>
                  <span className="filter-label">
                    {t('scenes.data_ingester.data_query.scene.labels.range')}
                  </span>
                  <RangePicker
                    className="date-range-picker"
                    format={dateFormat}
                    value={dateRange}
                    onChange={(dates) => {
                      if (dates) {
                        setDateRange([
                          dates[0] as dayjs.Dayjs,
                          dates[1] as dayjs.Dayjs,
                        ]);
                      } else {
                        setDateRange(null);
                      }
                    }}
                  />
                </Col>
              )}

              {period === 'MONTHLY' && (
                <Col>
                  <span className="filter-label">
                    {t('scenes.data_ingester.data_query.scene.labels.year')}
                  </span>
                  <Select
                    value={selectedYear}
                    className="filter-select"
                    onChange={(value) => setSelectedYear(value)}
                    showSearch
                    allowClear
                    placeholder="20##"
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option?.label
                        ?.toString()
                        .toLowerCase()
                        .includes(input.toLowerCase()) ?? false
                    }
                    options={yearOptions}
                  />
                </Col>
              )}

              {/* Botón de aplicar filtro */}
              <Col>
                <Button
                  type="primary"
                  className="apply-button"
                  disabled={isApplyDisabled || loading}
                  onClick={handleApplyFilter}
                >
                  {t(
                    'scenes.data_ingester.data_query.scene.buttons.apply_filter'
                  )}
                </Button>
              </Col>

              {/* Espacio flexible para alinear el botón a la derecha */}
              <Col flex="auto" />

              {/* Botón de exportar datos fuente */}
              <Col>
                <Button
                  type="primary"
                  icon={<CloudDownloadOutlined />}
                  onClick={() => setIsDownloadCsvModalVisible(true)}
                >
                  {t(
                    'scenes.data_ingester.data_query.scene.buttons.export_source_data'
                  )}
                </Button>
              </Col>
            </Row>
          </Card>
        </div>

        <Card style={{ marginTop: '12px' }}>
          {tableData.length > 0 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '16px',
                alignItems: 'center',
              }}
            >
              <div>
                <span
                  style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: '14px' }}
                >
                  {t(
                    'scenes.data_ingester.data_query.scene.messages.showing_results',
                    { count: tableData.length - 1 }
                  )}
                </span>
              </div>
              <div>
                <Button
                  type="default"
                  icon={<FileExcelOutlined />}
                  disabled={loading}
                  onClick={handleDownloadExcel}
                >
                  {t(
                    'scenes.data_ingester.data_query.scene.buttons.export_data'
                  )}
                </Button>
              </div>
            </div>
          )}
          <Spin spinning={loading && hasAppliedFilter}>
            {tableData.length > 0 ? (
              <Table
                dataSource={tableData}
                columns={tableColumns}
                pagination={false}
                rowKey="groupedby"
                bordered
                scroll={{ x: scrollXWidth, y: tableHeight }}
                sticky={stickyConfig}
              />
            ) : (
              <div className="empty-data-message">
                {loading ? (
                  t(
                    'scenes.data_ingester.data_query.scene.messages.loading_data'
                  )
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t(
                      'scenes.data_ingester.data_query.scene.empty.description'
                    )}
                  />
                )}
              </div>
            )}
          </Spin>
        </Card>
      </div>
      {/* Modal para descargar datos fuente */}
      <DownloadCsvModal
        isOpen={isDownloadCsvModalVisible}
        handleModalCancel={() => setIsDownloadCsvModalVisible(false)}
      />
    </Layout>
  );
}

export default DataQuery;
